import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errorText } from "../../lib/api";
import { SERVICE_OPTIONS, SITE, TEL } from "../../lib/site";
import { PhotoInput } from "./PhotoInput";
import { SlotPicker, humanDate } from "./SlotPicker";
import { newRequestId as newId } from "../../lib/api";

const VEHICLES = ["Voiture", "SUV / 4x4", "Utilitaire", "Camping-car", "Poids lourd", "Véhicule agricole", "Autre"];
const blank = { nom: "", prenom: "", telephone: "", email: "", ville: "", type_vehicule: "", marque: "", modele: "", immatriculation: "", service: "", date_souhaitee: "", creneau: "", message: "", consentement: false, website: "" };

function Group({ n, title, children }) {
  return (
    <fieldset className="rounded-[1.75rem] bg-white p-6 ring-1 ring-slate-200 sm:p-8">
      <legend className="sr-only">{title}</legend>
      <p className="mb-6 flex items-center gap-3 font-display text-xl font-extrabold text-ag-ink"><span className="grid h-8 w-8 place-items-center rounded-full bg-ag-blue font-mono text-sm text-white">{n}</span>{title}</p>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function F({ label, children, full }) {
  return <label className={`block ${full ? "sm:col-span-2" : ""}`}><span className="mb-1.5 block text-sm font-semibold text-slate-700">{label}</span>{children}</label>;
}

export function QuoteForm() {
  const [params] = useSearchParams();
  const rdv = params.get("mode") === "rendez-vous";
  const initService = SERVICE_OPTIONS.includes(params.get("service")) ? params.get("service") : "";
  const [f, setF] = useState({ ...blank, service: initService });
  const [pb, setPb] = useState([]);
  const [op, setOp] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [rid] = useState(newId);
  const [refresh, setRefresh] = useState(0);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (rdv && (!f.date_souhaitee || !f.creneau)) {
      toast.error("Merci de choisir une date et un horaire pour votre rendez-vous.");
      return;
    }
    setBusy(true);
    try {
      const body = { ...f, type: rdv ? "rendez-vous" : "devis", email: f.email || null, photos_parebrise: pb, photos_optique: op, request_id: rid,
        date_souhaitee: rdv ? f.date_souhaitee : null, creneau: rdv ? f.creneau : null };
      await api.post("/leads", body);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success(rdv ? "Demande de rendez-vous enregistrée." : "Demande de devis envoyée.");
    } catch (err) {
      if (err?.response?.status === 409) {
        setF((cur) => ({ ...cur, creneau: "" }));
        setRefresh((n) => n + 1);
      }
      toast.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (done && rdv) {
    return (
      <div className="rounded-[2rem] bg-white p-10 text-center ring-1 ring-slate-200" data-testid="quote-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-amber-500" />
        <h2 className="mt-5 font-display text-3xl font-extrabold text-ag-ink">Demande de rendez-vous enregistrée</h2>
        <p className="mx-auto mt-4 inline-block rounded-full bg-amber-50 px-4 py-1.5 text-sm font-bold text-amber-800 ring-1 ring-amber-200" data-testid="quote-success-status">En attente de confirmation</p>
        <p className="mt-4 text-lg font-semibold text-ag-navy first-letter:uppercase" data-testid="quote-success-slot">{humanDate(f.date_souhaitee, f.creneau)}</p>
        <p className="mt-3 text-slate-600">Notre équipe va vérifier et confirmer votre rendez-vous{f.email ? " : vous recevrez un email de confirmation" : " par téléphone"}. Pour toute question, appelez le <a href={TEL} className="font-bold text-ag-blue">{SITE.phone}</a>.</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="rounded-[2rem] bg-white p-10 text-center ring-1 ring-slate-200" data-testid="quote-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
        <h2 className="mt-5 font-display text-3xl font-extrabold text-ag-ink">Merci, votre demande est bien envoyée.</h2>
        <p className="mt-3 text-slate-600">Notre équipe vous recontacte pendant nos horaires d'ouverture. Pour une demande urgente, appelez le <a href={TEL} className="font-bold text-ag-blue">{SITE.phone}</a>.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="quote-full-form">
      <Group n="1" title="Vos coordonnées">
        <F label="Nom *"><input className="field" required value={f.nom} onChange={set("nom")} autoComplete="family-name" data-testid="quote-nom" /></F>
        <F label="Prénom"><input className="field" value={f.prenom} onChange={set("prenom")} autoComplete="given-name" data-testid="quote-prenom" /></F>
        <F label="Téléphone *"><input className="field" type="tel" required value={f.telephone} onChange={set("telephone")} autoComplete="tel" data-testid="quote-telephone" /></F>
        <F label="Email"><input className="field" type="email" value={f.email} onChange={set("email")} autoComplete="email" data-testid="quote-email" /></F>
        <F label="Ville" full><input className="field" value={f.ville} onChange={set("ville")} autoComplete="address-level2" data-testid="quote-ville" /></F>
      </Group>
      <Group n="2" title="Votre véhicule">
        <F label="Type de véhicule"><select className="field" value={f.type_vehicule} onChange={set("type_vehicule")} data-testid="quote-type-vehicule"><option value="">Sélectionner</option>{VEHICLES.map((v) => <option key={v}>{v}</option>)}</select></F>
        <F label="Immatriculation"><input className="field uppercase" value={f.immatriculation} onChange={set("immatriculation")} placeholder="AB-123-CD" data-testid="quote-immatriculation" /></F>
        <F label="Marque"><input className="field" value={f.marque} onChange={set("marque")} data-testid="quote-marque" /></F>
        <F label="Modèle"><input className="field" value={f.modele} onChange={set("modele")} data-testid="quote-modele" /></F>
      </Group>
      <Group n="3" title={rdv ? "Votre rendez-vous" : "Votre demande"}>
        <F label="Service souhaité *" full><select className="field" required value={f.service} onChange={set("service")} data-testid="quote-service"><option value="">Sélectionner un service</option>{SERVICE_OPTIONS.map((v) => <option key={v}>{v}</option>)}</select></F>
        {rdv && <SlotPicker date={f.date_souhaitee} time={f.creneau} refresh={refresh} onDate={(v) => setF((cur) => ({ ...cur, date_souhaitee: v }))} onTime={(v) => setF((cur) => ({ ...cur, creneau: v }))} />}
        {rdv && (
          <div className={`rounded-2xl p-5 sm:col-span-2 ${f.creneau ? "bg-ag-navy text-white" : "bg-ag-mist text-slate-600 ring-1 ring-ag-blue/10"}`} data-testid="quote-slot-recap" aria-live="polite">
            <p className="text-xs font-bold uppercase tracking-[0.16em] opacity-80">Récapitulatif de votre rendez-vous</p>
            <p className="mt-1 font-display text-xl font-extrabold first-letter:uppercase">{f.creneau ? humanDate(f.date_souhaitee, f.creneau) : "Aucun créneau sélectionné"}</p>
            {f.creneau && <p className="mt-1 text-sm text-blue-100">Votre demande sera en attente de confirmation par notre équipe.</p>}
          </div>
        )}
        <F label="Message" full><textarea className="field min-h-[120px]" value={f.message} onChange={set("message")} placeholder="Décrivez le dommage, sa position, vos questions…" data-testid="quote-message" /></F>
        <PhotoInput label="Photo du pare-brise" value={pb} onChange={setPb} testid="quote-photo-parebrise" />
        <PhotoInput label="Photo de l'optique" value={op} onChange={setOp} testid="quote-photo-optique" />
      </Group>
      <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} aria-hidden="true" />
      <label className="flex items-start gap-3 rounded-2xl bg-ag-mist p-5 text-sm text-slate-700">
        <input type="checkbox" required checked={f.consentement} onChange={set("consentement")} className="mt-1" data-testid="quote-consent" />
        <span>J'accepte que les données saisies soient utilisées par {SITE.name} pour répondre à ma demande. <a href="/politique-confidentialite" className="underline">Politique de confidentialité</a></span>
      </label>
      <button disabled={busy} className="btn btn-blue w-full justify-center py-5 text-base uppercase tracking-wide sm:w-auto sm:px-10" data-testid="quote-submit-button">
        {busy ? "Envoi en cours…" : rdv ? "Demander mon rendez-vous" : "Demander mon devis"}
      </button>
    </form>
  );
}
